import React, { useState } from "react";
import { Plus, Pencil, Trash2, DollarSign, Wallet } from "lucide-react";
import {
  Card,
  CardContent,
  CardDescription,
  CardFooter,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { WalletSettings, WalletData, MoneyTrackingEntry } from "@/utils/storage";

/** Round to cents, then format as a signed dollar string (e.g. "$5.00" or "-$2.50"). */
const formatSignedDollars = (amount: number): string => {
  const rounded = Math.round(amount * 100) / 100;
  if (rounded < 0) return `-$${Math.abs(rounded).toFixed(2)}`;
  return `$${rounded.toFixed(2)}`;
};

interface WalletTabProps {
  walletSettings: WalletSettings;
  walletData: WalletData;
  totalStarsEarned: number;
  currentStars: number;
  // Money tracking props
  moneyEntries?: MoneyTrackingEntry[];
  allowAddMoney?: boolean;
  editMode?: boolean;
  onAddEntry?: (entry: Omit<MoneyTrackingEntry, "id" | "dateAdded">) => void;
  onUpdateEntry?: (id: string, entry: Partial<MoneyTrackingEntry>) => void;
  onDeleteEntry?: (id: string) => void;
}

const WalletTab: React.FC<WalletTabProps> = ({
  walletSettings,
  walletData,
  totalStarsEarned: _totalStarsEarned,
  currentStars,
  moneyEntries = [],
  allowAddMoney = false,
  editMode = false,
  onAddEntry,
  onUpdateEntry,
  onDeleteEntry,
}) => {
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [selectedEntry, setSelectedEntry] = useState<MoneyTrackingEntry | null>(null);
  const [newTitle, setNewTitle] = useState("");
  const [newAmount, setNewAmount] = useState("");

  // Calculate current balance based on current stars, not total earned
  const currentBalance = currentStars / walletSettings.conversionRate;

  // Calculate totals for money tracking
  const entriesTotal = moneyEntries.reduce((sum, entry) => sum + entry.amount, 0);
  const appMoney = walletSettings?.enabled ? currentBalance : 0;
  const grandTotal = entriesTotal + appMoney;

  // Can add money if either in edit mode OR allowAddMoney is enabled
  const canAddMoney = editMode || allowAddMoney;

  const handleAddEntry = () => {
    const amount = parseFloat(newAmount);
    if (newTitle.trim() && !isNaN(amount) && amount !== 0 && onAddEntry) {
      onAddEntry({
        title: newTitle.trim(),
        amount: amount,
      });
      setNewTitle("");
      setNewAmount("");
      setShowAddDialog(false);
    }
  };

  const handleEditEntry = () => {
    if (!selectedEntry || !onUpdateEntry) return;
    const amount = parseFloat(newAmount);
    if (newTitle.trim() && !isNaN(amount) && amount !== 0) {
      onUpdateEntry(selectedEntry.id, {
        title: newTitle.trim(),
        amount: amount,
      });
      setSelectedEntry(null);
      setNewTitle("");
      setNewAmount("");
      setShowEditDialog(false);
    }
  };

  const handleDeleteEntry = () => {
    if (selectedEntry && onDeleteEntry) {
      onDeleteEntry(selectedEntry.id);
      setSelectedEntry(null);
      setShowDeleteDialog(false);
    }
  };

  const openEditDialog = (entry: MoneyTrackingEntry) => {
    setSelectedEntry(entry);
    setNewTitle(entry.title);
    setNewAmount(entry.amount.toString());
    setShowEditDialog(true);
  };

  const openDeleteDialog = (entry: MoneyTrackingEntry) => {
    setSelectedEntry(entry);
    setShowDeleteDialog(true);
  };

  // If wallet is not enabled, show a message
  if (!walletSettings.enabled) {
    return (
      <div className="text-center py-8">
        <div className="text-muted-foreground">
          <p className="text-lg mb-2">Wallet Feature Disabled</p>
          <p className="text-sm">
            Ask your parent to enable the wallet feature in the admin panel to
            start earning money for your stars!
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Grand Total Card - Shows total from all sources */}
      <Card className="relative overflow-hidden bg-gradient-to-br from-green-50 to-emerald-100 dark:from-green-950 dark:to-emerald-900 border-2 border-green-200 dark:border-green-800">
        <CardHeader className="text-center pb-2">
          <div className="flex items-center justify-center gap-3">
            <CardTitle className="text-2xl text-green-800 dark:text-green-100">Total Money</CardTitle>
            <div className="text-3xl">
              <DollarSign className="h-8 w-8 text-green-600 dark:text-green-400" />
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <div className={`text-5xl font-bold mb-3 ${Math.round(grandTotal * 100) < 0 ? "text-red-600 dark:text-red-400" : "text-green-700 dark:text-green-300"}`}>
            {formatSignedDollars(grandTotal)}
          </div>
          <CardDescription className="text-base text-green-600 dark:text-green-400">
            From all sources combined
          </CardDescription>
        </CardContent>
        <CardFooter className="text-center text-sm bg-green-100/50 dark:bg-green-900/30 py-4 flex flex-col gap-1">
          <div className="flex items-center justify-center gap-2 text-green-700 dark:text-green-300">
            <Wallet className="h-4 w-4" />
            <span>From Stars: <span className="font-semibold">${appMoney.toFixed(2)}</span></span>
            <span className="text-xs text-green-600 dark:text-green-400">({currentStars} stars)</span>
          </div>
          {Math.round(entriesTotal * 100) !== 0 && (
            <div className={Math.round(entriesTotal * 100) < 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}>
              From Other Sources: <span className="font-semibold">{formatSignedDollars(entriesTotal)}</span>
            </div>
          )}
        </CardFooter>
      </Card>

      {/* Wallet Info Card */}
      <Card className="relative overflow-hidden bg-gradient-to-br from-background to-muted/20 border-2">
        <CardHeader className="text-center">
          <div className="flex items-center justify-center gap-3">
            <CardTitle className="text-xl">Star Earnings</CardTitle>
            <div className="text-2xl relative">
              <span className="absolute -bottom-1 -right-1 text-lg"></span>
            </div>
          </div>
        </CardHeader>
        <CardContent className="text-center">
          <div className="text-3xl font-bold text-primary mb-2">
            ${currentBalance.toFixed(2)}
          </div>
          <CardDescription className="text-sm">
            {walletSettings.conversionRate} stars = $1.00
          </CardDescription>
        </CardContent>
        <CardFooter className="text-center text-sm text-muted-foreground bg-muted/30 py-3">
          <div className="w-full">
            Lifetime earned:{" "}
            <span className="font-semibold">
              ${walletData.lifetimeEarned.toFixed(2)}
            </span>
          </div>
        </CardFooter>
      </Card>

      {/* Add Money Source Button */}
      {canAddMoney && onAddEntry && (
        <Button
          onClick={() => {
            setNewTitle("");
            setNewAmount("");
            setShowAddDialog(true);
          }}
          className="w-full"
          variant="outline"
        >
          <Plus className="mr-2 h-4 w-4" />
          Add Money Entry
        </Button>
      )}

      {/* Money Sources List */}
      {moneyEntries.length > 0 && (
        <div className="space-y-3">
          <h3 className="text-lg font-semibold text-muted-foreground">Other Money Entries</h3>
          {moneyEntries.map((entry) => (
            <Card key={entry.id} className="transition-all hover:shadow-md">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h4 className="font-medium">{entry.title}</h4>
                    <p className="text-sm text-muted-foreground">
                      {Math.round(entry.amount * 100) < 0 ? "Expense" : "Income"} · Added {new Date(entry.dateAdded).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="flex items-center gap-3">
                    <span className={`text-xl font-bold ${Math.round(entry.amount * 100) < 0 ? "text-red-600 dark:text-red-400" : "text-green-600 dark:text-green-400"}`}>
                      {formatSignedDollars(entry.amount)}
                    </span>
                    {editMode && onUpdateEntry && onDeleteEntry && (
                      <div className="flex gap-1">
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openEditDialog(entry)}
                          className="h-8 w-8"
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <Button
                          variant="ghost"
                          size="icon"
                          onClick={() => openDeleteDialog(entry)}
                          className="h-8 w-8 text-red-500 hover:text-red-600"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    )}
                  </div>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}

      {/* Empty state when allowAddMoney is enabled but no entries */}
      {moneyEntries.length === 0 && canAddMoney && (
        <Card className="border-dashed">
          <CardContent className="py-6 text-center text-muted-foreground">
            <DollarSign className="h-10 w-10 mx-auto mb-3 opacity-50" />
            <p className="text-base mb-1">No money entries yet</p>
            <p className="text-sm">Click &quot;Add Money Entry&quot; to track income or expenses!</p>
          </CardContent>
        </Card>
      )}

      {/* Add Entry Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Add Money Entry</DialogTitle>
            <DialogDescription>
              Add income (positive) or an expense/deduction (negative) to track your money.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="title">Title</Label>
              <Input
                id="title"
                placeholder="e.g., Birthday money from Grandma"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="amount">Amount ($)</Label>
              <Input
                id="amount"
                type="number"
                step="0.01"
                placeholder="e.g., 5.00 or -2.50"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Use a negative number for expenses (e.g., -2.50)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleAddEntry}>Add</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Entry Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="sm:max-w-[425px]">
          <DialogHeader>
            <DialogTitle>Edit Money Entry</DialogTitle>
            <DialogDescription>
              Update the details for this entry. Use a negative amount for expenses.
            </DialogDescription>
          </DialogHeader>
          <div className="grid gap-4 py-4">
            <div className="grid gap-2">
              <Label htmlFor="edit-title">Title</Label>
              <Input
                id="edit-title"
                value={newTitle}
                onChange={(e) => setNewTitle(e.target.value)}
              />
            </div>
            <div className="grid gap-2">
              <Label htmlFor="edit-amount">Amount ($)</Label>
              <Input
                id="edit-amount"
                type="number"
                step="0.01"
                value={newAmount}
                onChange={(e) => setNewAmount(e.target.value)}
              />
              <p className="text-xs text-muted-foreground">Use a negative number for expenses (e.g., -2.50)</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleEditEntry}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Money Source</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete &quot;{selectedEntry?.title}&quot;? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handleDeleteEntry} className="bg-red-500 hover:bg-red-600">
              Delete
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

export default WalletTab;
